import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, BookOpen, FileCode2, FileText, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/shared/empty-state";
import { CardSkeleton } from "@/components/shared/loading-skeleton";
import { ROUTES } from "@/app/routes";
import { useAuth } from "@/features/auth/auth-provider";
import { toastMutationError } from "@/lib/utils/formatting";
import type { PlatformSkillDto, TenantSkillDto, TenantSkillFileInput } from "@/lib/api/generated/control-api.schemas";
import {
  getGetApiSkillLibraryQueryKey,
  getGetApiSkillsQueryKey,
  useGetApiSkillLibrary,
  useGetApiSkills,
  usePatchApiSkillLibraryPlatformSkillId,
  usePatchApiSkillsTenantSkillId,
  usePostApiSkillLibrary,
  usePostApiSkillLibraryPlatformSkillIdInstall,
  usePostApiSkills,
  usePostApiSkillsTenantSkillIdUpdateFromLibrary,
} from "@/lib/api/generated/skills/skills";

type SkillFileDraft = {
  relativePath: string;
  content: string;
};

type SkillFormState = {
  skillId?: string;
  skillKey: string;
  displayName: string;
  description: string;
  status: string;
  files: SkillFileDraft[];
  activePath: string;
};

type SkillEditorMode = "tenant" | "platform";

const defaultSkillMarkdown = `---
name: new-skill
description: Describe when this skill should be used.
---

# New Skill

## When To Use

- Use this skill when...

## Workflow

1. Check the current task.
2. Read only the referenced files that are needed.
3. Apply the procedure.
`;

const fileTemplates = [
  {
    label: "Reference",
    path: "references/notes.md",
    content: "# Notes\n\nAdd supporting reference material here.\n",
  },
  {
    label: "Script",
    path: "scripts/helper.py",
    content: "#!/usr/bin/env python3\n\n",
  },
  {
    label: "Example",
    path: "examples/example.md",
    content: "# Example\n\n",
  },
] as const;

export function SkillsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isPlatformAdmin } = useAuth();
  const { data: skills = [], isLoading } = useGetApiSkills();
  const libraryParams = isPlatformAdmin ? { includeDisabled: true } : undefined;
  const { data: librarySkills = [], isLoading: isLibraryLoading } = useGetApiSkillLibrary(libraryParams);
  const installSkill = usePostApiSkillLibraryPlatformSkillIdInstall();
  const updateInstalledSkill = usePostApiSkillsTenantSkillIdUpdateFromLibrary();
  const sorted = useMemo(
    () => [...skills].toSorted((left, right) => left.skillKey.localeCompare(right.skillKey)),
    [skills],
  );
  const sortedLibrary = useMemo(
    () => [...librarySkills].toSorted((left, right) => left.displayName.localeCompare(right.displayName)),
    [librarySkills],
  );
  const installedKeys = useMemo(() => new Set(skills.map((skill) => skill.skillKey)), [skills]);
  const libraryById = useMemo(
    () => new Map(librarySkills.map((skill) => [skill.platformSkillId, skill])),
    [librarySkills],
  );

  const install = (skill: PlatformSkillDto) => {
    installSkill.mutate(
      { platformSkillId: skill.platformSkillId },
      {
        onSuccess: (installed) => {
          void queryClient.invalidateQueries({ queryKey: getGetApiSkillsQueryKey() });
          void queryClient.invalidateQueries({ queryKey: getGetApiSkillLibraryQueryKey() });
          toast.success("Skill installed");
          navigate(ROUTES.tenantSkill("", installed.tenantSkillId));
        },
        onError: (error) => {
          toast.error(toastMutationError(error, "Failed to install skill"));
        },
      },
    );
  };

  const updateFromLibrary = (skill: TenantSkillDto) => {
    updateInstalledSkill.mutate(
      { tenantSkillId: skill.tenantSkillId },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetApiSkillsQueryKey() });
          toast.success("Skill updated from library");
        },
        onError: (error) => {
          toast.error(toastMutationError(error, "Failed to update skill"));
        },
      },
    );
  };

  if (isLoading || isLibraryLoading) {
    return <CardSkeleton />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: "var(--text-strong)" }}>
            Skills
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {isPlatformAdmin ? (
            <Button asChild variant="outline">
              <Link to={ROUTES.platformSkillNew()}>
                <Plus className="size-4" />
                New Library Skill
              </Link>
            </Button>
          ) : null}
          <Button asChild>
            <Link to={ROUTES.tenantSkillNew()}>
              <Plus className="size-4" />
              New Skill
            </Link>
          </Button>
        </div>
      </div>

      <Tabs defaultValue="skills" className="gap-4">
        <TabsList>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          <TabsTrigger value="library">Library</TabsTrigger>
        </TabsList>

        <TabsContent value="skills">
          {sorted.length === 0 ? (
            <EmptyState title="No skills" description="Create the first skill." />
          ) : (
            <div className="grid gap-3">
              {sorted.map((skill) => {
                const sourceSkill = skill.sourcePlatformSkillId
                  ? libraryById.get(skill.sourcePlatformSkillId)
                  : undefined;
                const hasLibraryUpdate =
                  sourceSkill?.status === "active" &&
                  skill.sourcePlatformSkillVersion != null &&
                  skill.sourcePlatformSkillVersion < sourceSkill.version;
                return (
                  <Card key={skill.tenantSkillId}>
                    <CardHeader className="gap-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <CardTitle className="flex items-center gap-2 text-base">
                            <BookOpen className="size-4" />
                            {skill.displayName}
                          </CardTitle>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {hasLibraryUpdate ? <Badge variant="secondary">Update available</Badge> : null}
                          {skill.status !== "active" ? <Badge variant="secondary">{skill.status}</Badge> : null}
                          {hasLibraryUpdate ? (
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => updateFromLibrary(skill)}
                              disabled={updateInstalledSkill.isPending}
                            >
                              Update
                            </Button>
                          ) : null}
                          <Button asChild type="button" size="sm" variant="outline">
                            <Link to={ROUTES.tenantSkill("", skill.tenantSkillId)}>Edit</Link>
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                      {skill.description ? (
                        <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                          {skill.description}
                        </p>
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="library">
          {sortedLibrary.length === 0 ? (
            <EmptyState title="No library skills" description="No platform skills are available to install." />
          ) : (
            <div className="grid gap-3">
              {sortedLibrary.map((skill) => {
                const installed = installedKeys.has(skill.skillKey);
                return (
                  <Card key={skill.platformSkillId}>
                    <CardHeader className="gap-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <CardTitle className="flex items-center gap-2 text-base">
                            <BookOpen className="size-4" />
                            {skill.displayName}
                          </CardTitle>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {isPlatformAdmin && skill.status !== "active" ? (
                            <Badge variant="secondary">{skill.status}</Badge>
                          ) : null}
                          {isPlatformAdmin ? (
                            <Button asChild type="button" size="sm" variant="outline">
                              <Link to={ROUTES.platformSkill("", skill.platformSkillId)}>Edit</Link>
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            size="sm"
                            disabled={installed || installSkill.isPending || skill.status !== "active"}
                            onClick={() => install(skill)}
                          >
                            {installed ? "Installed" : "Install"}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {skill.description ? (
                        <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                          {skill.description}
                        </p>
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function SkillEditorPage() {
  const { tenantSkillId } = useParams();
  const isNew = !tenantSkillId;
  const { data: skills = [], isLoading } = useGetApiSkills();
  const skill = isNew ? undefined : skills.find((item) => item.tenantSkillId === tenantSkillId);

  if (!isNew && isLoading) {
    return <CardSkeleton />;
  }

  if (!isNew && !skill) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <EmptyState title="Skill not found" description="The selected skill bundle does not exist." />
      </div>
    );
  }

  return <SkillEditorForm key={skill?.tenantSkillId ?? "tenant-new"} mode="tenant" skill={skill} />;
}

export function PlatformSkillEditorPage() {
  const { platformSkillId } = useParams();
  const isNew = !platformSkillId;
  const { data: skills = [], isLoading } = useGetApiSkillLibrary({ includeDisabled: true });
  const skill = isNew ? undefined : skills.find((item) => item.platformSkillId === platformSkillId);

  if (!isNew && isLoading) {
    return <CardSkeleton />;
  }

  if (!isNew && !skill) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <EmptyState title="Library skill not found" description="The selected platform skill template does not exist." />
      </div>
    );
  }

  return <SkillEditorForm key={skill?.platformSkillId ?? "platform-new"} mode="platform" skill={skill} />;
}

function SkillEditorForm({
  mode,
  skill,
}: {
  mode: SkillEditorMode;
  skill?: TenantSkillDto | PlatformSkillDto;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createSkill = usePostApiSkills();
  const updateSkill = usePatchApiSkillsTenantSkillId();
  const createPlatformSkill = usePostApiSkillLibrary();
  const updatePlatformSkill = usePatchApiSkillLibraryPlatformSkillId();
  const [form, setForm] = useState<SkillFormState>(() => skill ? createFormFromSkill(skill, mode) : createEmptyForm());
  const activeFile = form.files.find((file) => file.relativePath === form.activePath) ?? form.files[0];
  const validation = validateForm(form);
  const isPlatform = mode === "platform";
  const isSaving =
    createSkill.isPending || updateSkill.isPending || createPlatformSkill.isPending || updatePlatformSkill.isPending;

  const save = () => {
    if (validation) {
      toast.error(validation);
      return;
    }
    const files = normalizeFiles(form.files);
    const onTenantSuccess = (saved: TenantSkillDto) => {
      void queryClient.invalidateQueries({ queryKey: getGetApiSkillsQueryKey() });
      toast.success(form.skillId ? "Skill updated" : "Skill created");
      if (form.skillId) {
        setForm(createFormFromSkill(saved, "tenant"));
      } else {
        navigate(ROUTES.tenantSkill("", saved.tenantSkillId));
      }
    };
    const onPlatformSuccess = (saved: PlatformSkillDto) => {
      void queryClient.invalidateQueries({ queryKey: getGetApiSkillLibraryQueryKey() });
      toast.success(form.skillId ? "Library skill updated" : "Library skill created");
      if (form.skillId) {
        setForm(createFormFromSkill(saved, "platform"));
      } else {
        navigate(ROUTES.platformSkill("", saved.platformSkillId));
      }
    };
    const onError = (error: unknown) => {
      toast.error(toastMutationError(error, "Failed to save skill"));
    };

    if (isPlatform) {
      if (form.skillId) {
        updatePlatformSkill.mutate(
          {
            platformSkillId: form.skillId,
            data: {
              displayName: form.displayName.trim(),
              description: form.description.trim() || null,
              files,
              status: form.status,
            },
          },
          { onSuccess: onPlatformSuccess, onError },
        );
        return;
      }

      createPlatformSkill.mutate(
        {
          data: {
            skillKey: form.skillKey.trim(),
            displayName: form.displayName.trim(),
            description: form.description.trim() || null,
            files,
            status: form.status,
          },
        },
        { onSuccess: onPlatformSuccess, onError },
      );
      return;
    }

    if (form.skillId) {
      updateSkill.mutate(
        {
          tenantSkillId: form.skillId,
          data: {
            displayName: form.displayName.trim(),
            description: form.description.trim() || null,
            files,
            status: form.status,
          },
        },
        { onSuccess: onTenantSuccess, onError },
      );
      return;
    }

    createSkill.mutate(
      {
        data: {
          skillKey: form.skillKey.trim(),
          displayName: form.displayName.trim(),
          description: form.description.trim() || null,
          files,
          status: form.status,
        },
      },
      { onSuccess: onTenantSuccess, onError },
    );
  };

  const updateActiveFile = (patch: Partial<SkillFileDraft>) => {
    if (!activeFile) return;
    setForm((current) => ({
      ...current,
      activePath: patch.relativePath ?? current.activePath,
      files: current.files.map((file) =>
        file.relativePath === activeFile.relativePath ? { ...file, ...patch } : file,
      ),
    }));
  };

  const addFile = (template: (typeof fileTemplates)[number]) => {
    setForm((current) => {
      const used = new Set(current.files.map((file) => file.relativePath));
      let nextPath = template.path;
      for (let index = 2; used.has(nextPath); index += 1) {
        const dot = template.path.lastIndexOf(".");
        nextPath = dot > -1
          ? `${template.path.slice(0, dot)}-${index}${template.path.slice(dot)}`
          : `${template.path}-${index}`;
      }
      return {
        ...current,
        activePath: nextPath,
        files: [...current.files, { relativePath: nextPath, content: template.content }],
      };
    });
  };

  const removeActiveFile = () => {
    if (!activeFile || activeFile.relativePath === "SKILL.md") return;
    setForm((current) => {
      const files = current.files.filter((file) => file.relativePath !== activeFile.relativePath);
      return { ...current, files, activePath: files[0]?.relativePath ?? "SKILL.md" };
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-2">
          <BackLink />
          <div>
            <h1 className="text-xl font-semibold" style={{ color: "var(--text-strong)" }}>
              {form.skillId ? form.displayName || (isPlatform ? "Edit Library Skill" : "Edit Skill") : isPlatform ? "New Library Skill" : "New Skill"}
            </h1>
            <p className="mt-1 text-sm" style={{ color: "var(--muted-foreground)" }}>
              {isPlatform
                ? "Edit the platform template that tenants can install as their own skill."
                : form.skillId
                  ? "Edit the tenant skill files."
                  : "Create a tenant skill."}
            </p>
          </div>
        </div>
        <Button onClick={save} disabled={isSaving || Boolean(validation)}>
          <Save className="size-4" />
          {isSaving ? "Saving..." : form.skillId ? (isPlatform ? "Save Library Skill" : "Save Skill") : isPlatform ? "Create Library Skill" : "Create Skill"}
        </Button>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-4 pt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Key">
              <Input
                value={form.skillKey}
                disabled={Boolean(form.skillId)}
                onChange={(event) => setForm((current) => ({ ...current, skillKey: event.target.value }))}
                placeholder="sales.playbook"
              />
            </Field>
            <Field label="Name">
              <Input
                value={form.displayName}
                onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value }))}
                placeholder="Sales Playbook"
              />
            </Field>
          </div>
          <Field label="Description">
            <Input
              value={form.description}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              placeholder="How the agent qualifies leads"
            />
          </Field>
          <div className="flex items-center gap-2">
            <Switch
              id="skill-status"
              checked={form.status === "active"}
              onCheckedChange={(checked) =>
                setForm((current) => ({ ...current, status: checked ? "active" : "disabled" }))
              }
            />
            <Label htmlFor="skill-status">Active</Label>
          </div>

          <div className="flex flex-wrap gap-2">
            {fileTemplates.map((template) => (
              <Button key={template.label} variant="outline" size="sm" onClick={() => addFile(template)}>
                <Plus className="size-4" />
                Add {template.label}
              </Button>
            ))}
          </div>

          <div className="grid min-h-[560px] gap-3 lg:grid-cols-[220px_1fr]">
            <div className="flex flex-col gap-2 overflow-hidden rounded-md border p-2">
              {form.files.map((file) => (
                <button
                  key={file.relativePath}
                  type="button"
                  onClick={() => setForm((current) => ({ ...current, activePath: file.relativePath }))}
                  className={`flex items-center gap-2 rounded px-2 py-2 text-left text-xs ${
                    file.relativePath === activeFile?.relativePath ? "bg-[var(--bg-hover)]" : "hover:bg-[var(--bg-hover)]"
                  }`}
                >
                  {file.relativePath.toLowerCase().endsWith(".md") ? (
                    <FileText className="size-4" />
                  ) : (
                    <FileCode2 className="size-4" />
                  )}
                  <span className="min-w-0 flex-1 truncate font-mono">{file.relativePath}</span>
                </button>
              ))}
            </div>

            {activeFile ? (
              <Tabs defaultValue="editor" className="min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <TabsList>
                    <TabsTrigger value="editor">Editor</TabsTrigger>
                    <TabsTrigger value="preview">Preview</TabsTrigger>
                  </TabsList>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono">
                      {inferContentType(activeFile.relativePath)}
                    </Badge>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={removeActiveFile}
                      disabled={activeFile.relativePath === "SKILL.md"}
                      aria-label="Remove file"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
                <TabsContent value="editor" className="mt-3 flex flex-col gap-3">
                  <Field label="Path">
                    <Input
                      value={activeFile.relativePath}
                      disabled={activeFile.relativePath === "SKILL.md"}
                      onChange={(event) => updateActiveFile({ relativePath: event.target.value })}
                      className="font-mono"
                    />
                  </Field>
                  <Textarea
                    value={activeFile.content}
                    onChange={(event) => updateActiveFile({ content: event.target.value })}
                    rows={20}
                    className="min-h-[420px] resize-y font-mono text-xs"
                  />
                </TabsContent>
                <TabsContent value="preview" className="mt-3">
                  <pre className="min-h-[490px] overflow-auto rounded-md border bg-[var(--bg-subtle)] p-3 text-xs whitespace-pre-wrap">
                    {activeFile.content}
                  </pre>
                </TabsContent>
              </Tabs>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BackLink() {
  return (
    <Button asChild variant="ghost" size="sm" className="w-fit px-0 hover:bg-transparent">
      <Link to={ROUTES.tenantSkills()}>
        <ArrowLeft className="size-4" />
        Skills
      </Link>
    </Button>
  );
}

function createEmptyForm(): SkillFormState {
  return {
    skillKey: "",
    displayName: "",
    description: "",
    status: "active",
    files: [{ relativePath: "SKILL.md", content: defaultSkillMarkdown }],
    activePath: "SKILL.md",
  };
}

function createFormFromSkill(skill: TenantSkillDto | PlatformSkillDto, mode: SkillEditorMode): SkillFormState {
  const files = skill.files.length > 0
    ? skill.files.map((file) => ({
        relativePath: file.relativePath,
        content: file.content,
      }))
    : [{ relativePath: "SKILL.md", content: defaultSkillMarkdown }];
  return {
    skillId: mode === "tenant" ? (skill as TenantSkillDto).tenantSkillId : (skill as PlatformSkillDto).platformSkillId,
    skillKey: skill.skillKey,
    displayName: skill.displayName,
    description: skill.description ?? "",
    status: skill.status,
    files,
    activePath: files.some((file) => file.relativePath === "SKILL.md") ? "SKILL.md" : files[0].relativePath,
  };
}

function normalizeFiles(files: SkillFileDraft[]): TenantSkillFileInput[] {
  return files
    .map((file) => {
      const relativePath = file.relativePath.trim().replaceAll("\\", "/");
      return {
        relativePath,
        content: file.content,
        contentType: inferContentType(relativePath),
      };
    })
    .sort((left, right) => left.relativePath.localeCompare(right.relativePath));
}

function inferContentType(path: string) {
  const lower = path.toLowerCase();
  if (lower.endsWith(".md") || lower.endsWith(".mdx")) return "text/markdown";
  if (lower.endsWith(".py")) return "text/x-python";
  if (lower.endsWith(".ts") || lower.endsWith(".tsx")) return "text/typescript";
  if (lower.endsWith(".js") || lower.endsWith(".jsx") || lower.endsWith(".mjs")) return "text/javascript";
  if (lower.endsWith(".json")) return "application/json";
  if (lower.endsWith(".yaml") || lower.endsWith(".yml")) return "application/yaml";
  if (lower.endsWith(".txt")) return "text/plain";
  return "text/plain";
}

function validateForm(form: SkillFormState): string | null {
  if (!form.skillKey.trim()) return "Skill key is required";
  if (!form.displayName.trim()) return "Display name is required";
  if (!form.files.some((file) => file.relativePath.trim() === "SKILL.md")) {
    return "SKILL.md is required";
  }
  const paths = new Set<string>();
  for (const file of form.files) {
    const path = file.relativePath.trim().replaceAll("\\", "/");
    if (!path) return "Every file needs a path";
    if (path.startsWith("/") || path.includes("//") || path.split("/").some((part) => part === "." || part === ".." || !part)) {
      return `Invalid file path: ${file.relativePath}`;
    }
    if (paths.has(path)) return `Duplicate file path: ${path}`;
    paths.add(path);
    if (!file.content.trim()) return `${path} needs content`;
  }
  return null;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
